import React, { useCallback } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card,
  CardMedia, CardContent, Fade, Slide, Chip,
} from '@mui/material';
import {
  Apple as AppleIcon,
  MenuBook as MenuBookIcon,
  Translate as TranslateIcon,
  ChildCare as ChildCareIcon,
  WifiOff as WifiOffIcon,
  AutoStories as AutoStoriesIcon,
} from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';

const APP_STORE_URL = 'https://apps.apple.com/app/biblitz/id6739587923';

const screenshotSrcs = [
  '/assets/screenshots/01_closer_to_word.png',
  '/assets/screenshots/02_scripture.png',
  '/assets/screenshots/03_memorize.png',
  '/assets/screenshots/04_context.png',
  '/assets/screenshots/05_bilingual.png',
  '/assets/screenshots/06_chinese.png',
  '/assets/screenshots/07_families.png',
];

const featureIcons = [
  <MenuBookIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
  <AutoStoriesIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
  <TranslateIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
  <ChildCareIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
  <WifiOffIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
];

const HomePage = () => {
  const { t } = useLanguage();

  const handleDownload = useCallback(() => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f8f5ec 60%, #ffe082 100%)',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', maxWidth: '820px', mx: 'auto', mb: 6 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src="/assets/biblitz_logo.png"
                  alt="Biblitz"
                  sx={{
                    width: { xs: 90, md: 110 },
                    height: { xs: 90, md: 110 },
                    borderRadius: '22%',
                    boxShadow: '0 8px 32px rgba(34,51,108,0.18)',
                  }}
                />
              </Box>

              <Chip
                label={t.versionChip}
                sx={{
                  mb: 3,
                  bgcolor: 'secondary.light',
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, #22336c 0%, #d4af37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: '"Merriweather", serif',
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Biblitz
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  color: 'text.secondary',
                  fontFamily: '"Merriweather", serif',
                  fontWeight: 400,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              >
                {t.subtitle}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  fontFamily: '"Merriweather", serif',
                }}
              >
                {t.scriptureVerse}
                <br />
                <span style={{ fontSize: '0.95rem', color: '#b8860b' }}>{t.scriptureRef}</span>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  maxWidth: 680,
                  mx: 'auto',
                }}
              >
                {t.heroBody}
              </Typography>

              <Button
                variant="contained"
                size="large"
                startIcon={<AppleIcon />}
                onClick={handleDownload}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'secondary.main',
                  minWidth: 220,
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(34,51,108,0.25)',
                  '&:hover': { bgcolor: 'primary.dark', color: 'white' },
                }}
              >
                {t.downloadBtn}
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Screenshots Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Slide direction="up" in timeout={1200}>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  textAlign: 'center',
                  mb: 1,
                  color: 'primary.main',
                  fontFamily: '"Merriweather", serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.6rem', md: '2.2rem' },
                }}
              >
                {t.screenshotsTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{ textAlign: 'center', mb: 6, maxWidth: '600px', mx: 'auto', color: 'text.secondary' }}
              >
                {t.screenshotsBody}
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                {screenshotSrcs.map((src, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Fade in timeout={800 + index * 150}>
                      <Card
                        elevation={0}
                        sx={{
                          maxWidth: 240,
                          mx: 'auto',
                          border: '1px solid rgba(34,51,108,0.1)',
                          borderRadius: 3,
                          overflow: 'hidden',
                          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 12px 32px rgba(34,51,108,0.15)',
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={src}
                          alt={t.screenshots[index]?.caption}
                          sx={{ objectFit: 'cover', bgcolor: 'grey.50' }}
                        />
                        <CardContent sx={{ py: 1.5, px: 2, bgcolor: 'background.default' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem' }}
                          >
                            {t.screenshots[index]?.caption}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t.screenshots[index]?.sub}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Slide>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 1,
              color: 'primary.main',
              fontFamily: '"Merriweather", serif',
              fontWeight: 700,
              fontSize: { xs: '1.6rem', md: '2.2rem' },
            }}
          >
            {t.featuresTitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', mb: 7, maxWidth: '560px', mx: 'auto', color: 'text.secondary' }}
          >
            {t.featuresBody}
          </Typography>
          <Grid container spacing={4}>
            {t.features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in timeout={800 + index * 150}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      border: '1px solid rgba(212,175,55,0.25)',
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                      transition: 'box-shadow 0.3s',
                      '&:hover': { boxShadow: '0 4px 20px rgba(212,175,55,0.2)' },
                    }}
                  >
                    <Box sx={{ mb: 1.5 }}>{featureIcons[index]}</Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontFamily: '"Merriweather", serif' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {feature.body}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(90deg, #22336c 0%, #101a3a 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1500}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  color: 'secondary.main',
                  fontFamily: '"Merriweather", serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.8rem', md: '2.4rem' },
                }}
              >
                {t.ctaTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, color: 'rgba(255,255,255,0.75)', maxWidth: '500px', mx: 'auto', lineHeight: 1.8 }}
              >
                {t.ctaBody}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AppleIcon />}
                onClick={handleDownload}
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'primary.main',
                  minWidth: 220,
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'secondary.light', color: 'primary.dark' },
                }}
              >
                {t.downloadBtn}
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.4)' }}>
                {t.ctaCaption}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mt: 4, 
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.7rem',
                  fontStyle: 'italic'
                }}
              >
                Scripture: Berean Standard Bible (BSB) & 和合本 (CUV) - Public Domain
                <br />
                © 2026 CuratedData LLC
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;